// SVG картинки «Поделиться» → PNG 1080×1350 в браузере.
//
// Картинка, загруженная как <img>, не видит шрифтов страницы и не ходит за
// внешними файлами, поэтому Nunito Sans (те же файлы /fonts/, что у отчётов,
// reportSharedCss.ts) вшивается в SVG data:-URI перед отрисовкой. Шрифт не
// загрузился — рисуем запасным (Segoe UI / Roboto / Arial), картинка всё
// равно получается.
import { FONT_SLOT, SHARE_H, SHARE_W } from "@/lib/partner-share-card";

/** Nunito Sans (переменный, 200–1000): латиница, латиница-расширенная (ə ş ğ), кириллица. */
export const SHARE_FONTS: { file: string; range: string }[] = [
  {
    file: "/fonts/pe0TMImSLYBIv1o4X1M8ce2xCx3yop4tQpF_MeTm0lfGWVpNn64CL7U8upHZIbMV51Q42ptCp7t1R-s.woff2",
    range: "U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD",
  },
  {
    file: "/fonts/pe0TMImSLYBIv1o4X1M8ce2xCx3yop4tQpF_MeTm0lfGWVpNn64CL7U8upHZIbMV51Q42ptCp7t7R-tCKQ.woff2",
    range: "U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF",
  },
  {
    file: "/fonts/pe0TMImSLYBIv1o4X1M8ce2xCx3yop4tQpF_MeTm0lfGWVpNn64CL7U8upHZIbMV51Q42ptCp7txR-tCKQ.woff2",
    range: "U+0301,U+0400-045F,U+0490-0491,U+04B0-04B1,U+2116",
  },
];

/** @font-face для вшивания: data — base64 файла шрифта в том же порядке, что SHARE_FONTS. */
export function fontFaceCss(data: (string | null)[]): string {
  return SHARE_FONTS.map((f, i) =>
    data[i]
      ? `@font-face{font-family:'Nunito Sans';font-style:normal;font-weight:200 1000;src:url(data:font/woff2;base64,${data[i]}) format('woff2');unicode-range:${f.range}}`
      : "",
  ).join("");
}

export const withFonts = (svg: string, css: string) => svg.replace(FONT_SLOT, css);

async function fileBase64(url: string): Promise<string | null> {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const bytes = new Uint8Array(await r.arrayBuffer());
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return btoa(bin);
  } catch {
    return null;
  }
}

/** Скачать SVG с маршрута, вшить шрифт, нарисовать на canvas → PNG 1080×1350. */
export async function shareCardPng(svgUrl: string): Promise<Blob> {
  const res = await fetch(svgUrl);
  if (!res.ok) throw new Error(`share card ${res.status}`);
  return svgToPng(await res.text(), SHARE_W, SHARE_H);
}

/**
 * SVG-строка (с FONT_SLOT) → PNG w×h на canvas. Общая для «Поделиться» и
 * визитки (partner-visit-card.ts): шрифт вшивается одинаково.
 */
export async function svgToPng(svg: string, width: number, height: number): Promise<Blob> {
  const fonts = await Promise.all(SHARE_FONTS.map((f) => fileBase64(f.file)));
  const src = URL.createObjectURL(new Blob([withFonts(svg, fontFaceCss(fonts))], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    img.decoding = "async";
    await new Promise<void>((ok, fail) => {
      img.onload = () => ok();
      img.onerror = () => fail(new Error("svg decode"));
      img.src = src;
    });
    // Шрифт внутри SVG-картинки иногда дорисовывается на следующем кадре.
    await new Promise((r) => setTimeout(r, 60));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.drawImage(img, 0, 0, width, height);
    return await new Promise<Blob>((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error("toBlob"))), "image/png"));
  } finally {
    URL.revokeObjectURL(src);
  }
}
